/* -------------------------------------------------------------------------- */
/*                                   thunks                                   */
/* -------------------------------------------------------------------------- */
import { createAsyncThunk } from "@reduxjs/toolkit";

// constants
import {
    REDUCERS,
    QUEUES
} from "frontend/components/fort/mod/photos/constants.js";

// redux
import { batchesSlice } from "./index";
import { messagesSlice } from "../messagesSlice";

// scripts
import { batchFetcher } from "./scripts/batchFetcher";
import { assignBatchToMod } from "./scripts/clientApi";

/**
 * fetchBatchesThunk Redux RTK Async Thunk payloadCreator
 *  - determines what batches to fetch from the server
 *  - handles paginated requests, load more (loading 10 at a time)
 *  - if getAllPages loads all chosen pages, and adjusts for offsets (e.g. remove add a batch impacts pagination)
 *  - enhances batches, adding props used for local (e.g. batch photo counts, uploadIds)
 *  @param {Object} props
 *      {
 *          moderationType,
 *          showOnlySuspendedCreators,
 *      }
 *  @param {Object} thunkAPI comes with createAsyncThunk (e,g state, dispatch)
 *  @returns enhancedBatch : enhanced batches which batchEntityAdapter resolves to entities and ids.
 */
const fetchBatchesThunk = async (
    {
        moderationType = "ANY",
        showOnlySuspendedCreators = false,
        currentQueue = QUEUES.VIS_MOD,
        pages = 1,
        setUrlSearchParam,
        currentUser
    } = {},
    thunkAPI
) => {
    const { dispatch, getState } = thunkAPI;

    // get current state of our pagination and filters
    const state = getState();

    const {
        filters: { textSearch, sortBy, batchType },
        pagination: { batchesPerPage }
    } = state.batches;

    // Batch Fetcher
    const bf = batchFetcher({
        batchesPerPage,
        moderationType,
        showOnlySuspendedCreators,
        textSearch,
        batchType,
        sortBy,
        currentQueue,
        currentUser
    });

    let fetchedData = await bf.getAllPages(pages);
    let { enhancedBatches, newTotalCount, newCurrentPage } = fetchedData;

    // dispatch some things
    dispatch(
        batchesSlice.actions.setPagination({
            currentPage: newCurrentPage,
            totalCount: newTotalCount
        })
    );

    // In case user gave us an invalid page in URL Param, lets correct the URL
    setUrlSearchParam?.({
        key: "currentPage",
        value: newCurrentPage
    });
    dispatch(batchesSlice.actions.setHasLoaded(true)); // this one is only for the first load
    dispatch(batchesSlice.actions.setShowLoading(false));
    dispatch(batchesSlice.actions.setIsProcessing(false));

    return enhancedBatches;
};

// https://redux-toolkit.js.org/api/createAsyncThunk
export const fetchBatches = createAsyncThunk(
    `${REDUCERS.BATCHES}/fetchBatches`,
    fetchBatchesThunk
);

/*---------------------------
| Assign Batch To Moderator or Unassigned (null)
---------------------------*/
const assignBatchToModeratorThunk = async (
    { photoBatchId, moderatorId, currentUser, currentQueue },
    thunkAPI
) => {
    const { dispatch } = thunkAPI;

    const assignBatchToModResp = await assignBatchToMod({
        photoBatchId,
        moderatorId
    });

    const {
        assign: { ok, errors, photoBatch }
    } = assignBatchToModResp;

    if (!ok) {
        dispatch(
            messagesSlice.actions.addMessage({
                text:
                    errors || "Something went wrong with moderator assignment",
                timed: false,
                infoType: "error"
            })
        );
    } else {
        dispatch(
            messagesSlice.actions.addMessage({
                text: "Batch Assigned Successfully",
                timed: true,
                timeout: 2000,
                infoType: "success"
            })
        );

        return { batch: photoBatch, currentUser, currentQueue };
    }
};

export const assignBatchToModerator = createAsyncThunk(
    `${REDUCERS.BATCHES}/assignBatchToModerator`,
    assignBatchToModeratorThunk
);
